import { useCurrentMode } from '@app/styled';
import { Alert, Button, Flex, Link, Modal, ModalActions } from '@lokalise/louis';
import type { ReactNode } from 'react';

import type { FileFormat } from '../../types';
import ExampleImageSrc from './static/pr_example.png';
import ExampleDarkImageSrc from './static/pr_example_dark.png';
import { ExampleImage, ExampleImageWrapper, GitCompleteText } from './styles';
import type ExportButtonsMode from './types';
import { isBuildDownloadVisible, isPreviewButtonVisible } from './utils/visibilityHelpers';

interface DownloadProps {
    mode: ExportButtonsMode;
    fileFormat: FileFormat;
    file: null | string;
    error: ReactNode;
    gitIntegrationEnabled: boolean;
    onCloseGeneratedFileModal: () => void;
    onCloseErrorModal: () => void;
    onDownload: () => void;
    onGenerate: () => void;
    onPreview: () => void;
    onDownloadHandler: () => void;
}

const ExportButtons = ({
    mode,
    fileFormat,
    file,
    error,
    gitIntegrationEnabled,
    onCloseGeneratedFileModal,
    onCloseErrorModal,
    onDownload,
    onGenerate,
    onPreview,
    onDownloadHandler,
}: DownloadProps) => {
    const isDarkmode = useCurrentMode() === 'dark';

    const buttonSize = mode === 'quick' ? 'sm' : 'md';

    return (
        <Flex gap={1}>
            <Modal opened={!!error} onClose={onCloseErrorModal}>
                <Alert status="error">{error}</Alert>
                <ModalActions>
                    <Button variant="primary" onClick={onCloseErrorModal}>
                        OK
                    </Button>
                </ModalActions>
            </Modal>

            {isBuildDownloadVisible(fileFormat) && (
                <Button variant="primary" onClick={onDownload} size={buttonSize} data-metric="download-button">
                    Build and download
                </Button>
            )}

            <Button variant="secondary" onClick={onGenerate} size={buttonSize} data-metric="build-button">
                {mode === 'quick' ? 'Build' : 'Build only'}
            </Button>
            <Modal opened={!!file && !gitIntegrationEnabled} onClose={onCloseGeneratedFileModal}>
                <Flex justify="center">
                    <div>
                        Hurray, all done!{' '}
                        {file && (
                            <Link href={file} onClick={onDownloadHandler} download>
                                Download bundle.
                            </Link>
                        )}
                    </div>
                </Flex>
                <ModalActions>
                    <Button variant="primary" onClick={onCloseGeneratedFileModal}>
                        OK
                    </Button>
                </ModalActions>
            </Modal>

            <Modal
                opened={!!file && gitIntegrationEnabled}
                title="👏 Hurray, all done!"
                size="wide"
                onClose={onCloseGeneratedFileModal}
            >
                <Flex gap={7}>
                    <GitCompleteText>
                        Now check your repository for new translations pull request
                        {file && (
                            <>
                                {' '}
                                or{' '}
                                <Link href={file} download>
                                    download bundle
                                </Link>
                            </>
                        )}
                        .
                    </GitCompleteText>
                    <ExampleImageWrapper>
                        <ExampleImage src={isDarkmode ? ExampleDarkImageSrc : ExampleImageSrc} />
                    </ExampleImageWrapper>
                </Flex>
                <ModalActions>
                    <Button variant="secondary" onClick={onCloseGeneratedFileModal}>
                        Close
                    </Button>
                </ModalActions>
            </Modal>

            {isPreviewButtonVisible(fileFormat) && (
                <Button variant="secondary" onClick={onPreview} size={buttonSize}>
                    Preview
                </Button>
            )}
        </Flex>
    );
};

export default ExportButtons;
